"use client";

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useLocale, useTranslations } from "next-intl";
import BookingCalendar from "./booking-calendar";
import { useSearchParams } from "next/navigation";
import { trackLead } from "@/lib/analytics";
import { DEFAULT_SERVICE, SERVICES, TIME_SLOT_VALUES, findFirstAvailableDate, isSlotAvailable, type Availability, type ServiceId } from "./booking-data";
import { toDayKey } from "@/lib/booking-slots";
import { CalendarCheckIcon, CalendarClockIcon, CalendarIcon, CheckCircleIcon, ClockIcon, LayersIcon, ShieldIcon, TimerIcon, UserIcon, WalletIcon, WhatsappIcon } from "./booking-icons";
const WHATSAPP_NUMBER = "963956495873";
const CARD_SURFACE = "rounded-3xl bg-[linear-gradient(230.97deg,rgba(4,209,241,0.04)_-23.56%,rgba(124,81,189,0.04)_89.91%,rgba(186,36,149,0.04)_268.11%)] bg-[#080D16]";
const CARD = `border border-white/10 ${CARD_SURFACE}`;

/** The summary rail is outlined with a dashed hairline in the design. */
const CARD_DASHED = `border border-dashed border-white/40 ${CARD_SURFACE}`;

/** Inset panels (calendar, slot list, inputs) sit a shade darker than CARD. */
const PANEL = "rounded-2xl border border-white/8 bg-[#070B14]";
const FIELD = "w-full min-h-13 rounded-xl border border-white/10 bg-[#070B14] px-4 py-3 text-[14px] text-white outline-none transition-colors duration-200 placeholder:text-white/28 focus:border-cyan-500/70";

/** Flat accents measured off the design (no gradients on these surfaces). */
const ACCENT = "#0CA5F0";
const ACCENT_BUTTON = "#2295CE";
type FormState = {
  name: string;
  email: string;
  company: string;
  phone: string;
  notes: string;
};
const emptyForm: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  notes: ""
};

/**
 * "Today" only exists in the browser: the page is statically generated, so a
 * build-time date would go stale and break hydration. `useSyncExternalStore`
 * gives us `null` on the server and the real day once hydrated.
 */
let cachedDayKey: string | null = null;
function subscribeToDay() {
  return () => {};
}
function getDayKey(): string {
  const now = new Date();
  const key = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  if (key !== cachedDayKey) {
    cachedDayKey = key;
  }
  return cachedDayKey;
}
function getServerDayKey(): string | null {
  return null;
}
function firstAvailableSlot(date: Date | null, availability: Availability | null): number | null {
  const index = TIME_SLOT_VALUES.findIndex((_, slot) => isSlotAvailable(date, slot, availability));
  return index === -1 ? null : index;
}
export default function BookingExperience() {
  const t = useTranslations("Booking");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const intlLocale = isArabic ? "ar-u-nu-latn" : locale;
  const timeSlotLabels = t.raw("timeSlots") as string[];
  const benefits = t.raw("benefits") as string[];
  const stepperLabels = t.raw("stepper") as string[];

  // A service card on the home page links here with ?service=<id> so the
  // visitor lands on the right consultation type instead of re-picking it.
  const searchParams = useSearchParams();
  const requestedService = searchParams.get("service");
  const initialService: ServiceId = SERVICES.some(entry => entry.id === requestedService) ? requestedService as ServiceId : DEFAULT_SERVICE;
  const [service, setService] = useState<ServiceId>(initialService);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [showError, setShowError] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Honeypot. Hidden from people; bots fill it and we drop the submission. */
  const [website, setWebsite] = useState("");

  /** Which slots are already taken. Null until the first answer arrives. */
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [availabilityNonce, setAvailabilityNonce] = useState(0);
  /** Set once the availability endpoint has failed, retry included. */
  const [availabilityFailed, setAvailabilityFailed] = useState(false);
  const dayKey = useSyncExternalStore(subscribeToDay, getDayKey, getServerDayKey);
  const today = useMemo(() => {
    if (!dayKey) {
      return null;
    }
    const [year, month, day] = dayKey.split("-").map(Number);
    return new Date(year, month, day);
  }, [dayKey]);

  // The picked date/time/month default to the first bookable slot and are only
  // replaced once the visitor overrides them.
  const [dateOverride, setDateOverride] = useState<Date | null>(null);
  const [monthOverride, setMonthOverride] = useState<Date | null>(null);
  const [slotOverride, setSlotOverride] = useState<number | null>(null);
  const defaultDate = useMemo(() => today ? findFirstAvailableDate(today, availability) : null, [today, availability]);
  const selectedDate = dateOverride ?? defaultDate;
  const month_0 = monthOverride ?? (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : null);
  // Until the endpoint answers we do not know what is free, so the slot list
  // shows placeholders and the form refuses to submit instead of guessing.
  const isCheckingAvailability = availability === null && !availabilityFailed;
  const selectedSlot = slotOverride ?? (isCheckingAvailability ? null : firstAvailableSlot(selectedDate, availability));

  // Pull real availability for the month on screen, plus a month either side so
  // paging the calendar does not flash. Refetched whenever the visitor moves
  // month, and after a clash, so a slot someone else just took disappears.
  const rangeFrom = month_0 ? toDayKey(new Date(month_0.getFullYear(), month_0.getMonth() - 1, 1)) : null;
  const rangeTo = month_0 ? toDayKey(new Date(month_0.getFullYear(), month_0.getMonth() + 2, 0)) : null;
  useEffect(() => {
    if (!rangeFrom || !rangeTo) {
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      const response = await fetch(`/api/availability?from=${rangeFrom}&to=${rangeTo}`, {
        signal: controller.signal,
        cache: "no-store"
      });
      if (!response.ok) {
        throw new Error(`availability ${response.status}`);
      }
      const data = (await response.json()) as Partial<Availability> & {
        ok?: boolean;
      };
      if (!data?.ok || !data.taken || !data.now) {
        throw new Error("availability malformed");
      }
      return {
        taken: data.taken,
        now: data.now
      };
    };
    (async () => {
      // One retry covers a dropped request; a second failure is reported to
      // the visitor rather than swallowed, so an outage cannot quietly show
      // the whole week as free.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const next = await load();
          if (controller.signal.aborted) return;
          setAvailability(next);
          setAvailabilityFailed(false);
          return;
        } catch {
          if (controller.signal.aborted) return;
          if (attempt === 0) {
            await new Promise(resolve => setTimeout(resolve, 1200));
            if (controller.signal.aborted) return;
          }
        }
      }
      setAvailabilityFailed(true);
    })();
    return () => controller.abort();
  }, [rangeFrom, rangeTo, availabilityNonce]);
  const retryAvailability = () => {
    setAvailabilityFailed(false);
    setAvailabilityNonce(nonce => nonce + 1);
  };
  const dateLabel = useMemo(() => {
    if (!selectedDate) {
      return null;
    }
    return new Intl.DateTimeFormat(intlLocale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(selectedDate);
  }, [intlLocale, selectedDate]);
  const timeLabel = selectedSlot === null ? null : timeSlotLabels[selectedSlot] ?? null;
  const hasDetails = Boolean(form.name.trim() && form.email.trim() && form.phone.trim());
  const hasSchedule = Boolean(selectedDate && selectedSlot !== null);
  const hasStartedDetails = Object.values(form).some(value => value.trim());

  // Step 1 stays active on arrival — the date/time defaults must not make the
  // stepper look like the visitor has already moved past picking a service.
  const currentStep = isConfirmed ? 4 : !hasStartedDetails ? 1 : !hasDetails ? 2 : !hasSchedule ? 3 : 4;
  const handleSelectDate = (date: Date) => {
    setDateOverride(date);
    // Fall back to the new day's first free slot instead of keeping a stale one.
    setSlotOverride(null);
    setIsConfirmed(false);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCheckingAvailability) {
      return;
    }
    if (!hasSchedule || !hasDetails || isSubmitting) {
      if (!hasSchedule || !hasDetails) setShowError(true);
      return;
    }
    setShowError(false);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const response_0 = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...form,
          website,
          service,
          date: selectedDate ? toDayKey(selectedDate) : "",
          time: selectedSlot === null ? "" : TIME_SLOT_VALUES[selectedSlot],
          locale,
          source: "booking"
        })
      });
      const result = (await response_0.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (!response_0.ok || !result?.ok) {
        if (result?.error === "slot_taken" || result?.error === "slot_past") {
          // Someone else got there first — pull fresh availability so the slot
          // they took disappears from the list straight away.
          setAvailabilityNonce(nonce_0 => nonce_0 + 1);
          setSlotOverride(null);
          setSubmitError(t("errorSlotTaken"));
          return;
        }
        setSubmitError(result?.error === "invalid_email" ? t("errorInvalidEmail") : result?.error === "rate_limited" ? t("errorRateLimited") : t("errorGeneric"));
        return;
      }
      trackLead({
        service,
        locale
      });
      setIsConfirmed(true);
    } catch {
      setSubmitError(t("errorNetwork"));
    } finally {
      setIsSubmitting(false);
    }
  };
  const notProvided = t("notProvided");
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t("whatsappMessage", {
    service: t(`services.${service}.title`),
    date: dateLabel ?? notProvided,
    time: timeLabel ?? notProvided,
    name: form.name || notProvided,
    company: form.company || notProvided,
    phone: form.phone || notProvided,
    notes: form.notes || notProvided
  }))}`;
  const summaryRows = [{
    key: "date",
    icon: CalendarIcon,
    label: t("summaryDate"),
    value: dateLabel ?? t("summaryEmpty")
  }, {
    key: "time",
    icon: ClockIcon,
    label: t("summaryTime"),
    value: timeLabel ?? t("summaryEmpty")
  }, {
    key: "duration",
    icon: TimerIcon,
    label: t("summaryDuration"),
    value: t("summaryDurationValue")
  }, {
    key: "price",
    icon: WalletIcon,
    label: t("summaryPrice"),
    value: t("summaryPriceValue")
  }];
  return <section id="booking" dir={isArabic ? "rtl" : "ltr"} className="relative px-4 py-8 md:px-6 lg:px-8 lg:py-10">
      <div className="mx-auto flex w-full max-w-310 flex-col gap-8 lg:gap-12">
        {/* ── Progress stepper ─────────────────────────────────────────── */}
        <ol aria-label={t("stepperLabel")} className={`m-0 flex list-none flex-col gap-5 rounded-3xl border border-white/10 px-5 py-5 sm:flex-row sm:items-center sm:gap-0 sm:px-8 ${CARD}`}>
          {stepperLabels.map((label, index) => {
          const step = index + 1;
          const isActive = step === currentStep;
          const isDone = step < currentStep;
          return <li key={label} aria-current={isActive ? "step" : undefined} className="flex flex-1 items-center gap-3 last:flex-none sm:gap-5">
                {/* On the horizontal track the design puts the number badge
                    *after* the label so the connector leaves from the badge.
                    Stacked on mobile the badges read better in a column, so
                    the swap only kicks in from `sm` up. */}
                <span className={`flex shrink-0 items-center gap-3 ${isArabic ? "sm:flex-row-reverse" : ""}`}>
                  <span className={`flex size-14 shrink-0 items-center justify-center rounded-full text-[20px] font-bold transition-colors duration-300 ${isActive ? "text-white shadow-[0_0_28px_rgba(12,165,240,0.45)]" : isDone ? "border border-cyan-500/60 bg-cyan-500/12 text-cyan-500" : "bg-white/5 text-white/60 shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_0_26px_rgba(255,255,255,0.04)]"}`} style={isActive ? {
                backgroundColor: ACCENT
              } : undefined}>
                    {isDone ? <CheckCircleIcon className="size-6" /> : `0${step}`}
                  </span>

                  <span className={`text-[16px] whitespace-nowrap transition-colors duration-300 ${isActive ? "font-bold text-white" : isDone ? "font-medium text-white/70" : "text-white/50"}`}>
                    {label}
                  </span>
                </span>

                {step < stepperLabels.length ? <span aria-hidden="true" className={`hidden h-px flex-1 sm:mx-3 sm:block ${step <= currentStep ? "bg-cyan-500" : "bg-white/16"}`} /> : null}
              </li>;
        })}
        </ol>

        <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_306px] lg:gap-10">
          {/* ── Main column ────────────────────────────────────────────── */}
          <div className="flex min-w-0 flex-col gap-8 lg:gap-12">
            {/* Service picker */}
            <fieldset className={`m-0 flex flex-col gap-5 border-0 p-5 sm:p-6 ${CARD}`}>
              <legend className="sr-only">{t("serviceTitle")}</legend>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <CalendarIcon className="size-5 text-white" />
                  <h2 className="m-0 text-[18px] font-bold leading-[1.6] text-white">
                    {t("serviceTitle")}
                  </h2>
                </div>
                <p className="m-0 text-[14px] leading-[1.8] text-white/44">
                  {t("serviceSubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2 xl:grid-cols-4">
                {SERVICES.map(({
                id,
                icon: ServiceIcon
              }) => {
                const isSelected = service === id;
                return <label key={id} className={`relative flex min-h-62 cursor-pointer flex-col items-center rounded-2xl border px-3 pt-11 pb-10 text-center transition-all duration-300 ${isSelected ? "border-2 border-cyan-500 bg-[#080C15] shadow-[0_10px_30px_rgba(4,209,241,0.12)]" : "border-white/10 bg-[#080A16] hover:border-white/22"}`}>
                      <input type="radio" name="service" value={id} checked={isSelected} onChange={() => {
                    setService(id);
                    setIsConfirmed(false);
                  }} className="sr-only" />

                      <span aria-hidden="true" className={`absolute top-4 end-4 flex size-5 items-center justify-center rounded-[6px] border transition-colors duration-300 ${isSelected ? "border-cyan-500 bg-cyan-500" : "border-white/28 bg-transparent"}`}>
                        {isSelected ? <svg viewBox="0 0 12 12" className="size-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="m2.5 6.2 2.2 2.2 4.8-4.8" />
                          </svg> : null}
                      </span>

                      {/* Soft disc behind the glyph — the design's icons sit on
                          a radial pool of light, not on a bordered tile. */}
                      <span className="flex size-19 shrink-0 items-center justify-center rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.07)_0%,rgba(255,255,255,0)_70%)]">
                        <ServiceIcon className={`size-10 transition-colors duration-300 ${isSelected ? "text-white" : "text-white/85"}`} />
                      </span>

                      <span className="mt-7 flex flex-col gap-2">
                        <span className="text-[16px] font-bold leading-[1.5] text-white">
                          {t(`services.${id}.title`)}
                        </span>
                        <span className="text-[15px] text-white/50">
                          {t(`services.${id}.duration`)}
                        </span>
                      </span>
                    </label>;
              })}
              </div>
            </fieldset>

            {/* Date + time */}
            <div className={`flex flex-col gap-5 p-5 sm:p-6 ${CARD}`}>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <CalendarIcon className="size-5 text-white" />
                  <h2 className="m-0 text-[18px] font-bold leading-[1.6] text-white">
                    {t("scheduleTitle")}
                  </h2>
                </div>
                <p className="m-0 text-[14px] leading-[1.8] text-white/44">
                  {t("scheduleSubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,41fr)_minmax(0,59fr)]">
                {/* Calendar stays first for assistive tech, but the design
                    places it after the slot list on wide screens. */}
                <div className="lg:order-2">
                  {today && month_0 ? <BookingCalendar locale={locale} isArabic={isArabic} month={month_0} selected={selectedDate} today={today} onMonthChange={setMonthOverride} onSelect={handleSelectDate} availability={availability} /> : <div className={`flex min-h-92 items-center justify-center p-5 text-[13px] text-white/44 ${PANEL}`}>
                      {t("calendarLoading")}
                    </div>}
                </div>

                <fieldset className={`m-0 flex flex-col gap-5 self-start p-5 sm:p-6 lg:order-1 ${PANEL}`}>
                  <legend className="sr-only">{t("timesTitle")}</legend>
                  <div className="flex items-center gap-2.5">
                    <CalendarIcon className="size-5 text-white" />
                    <span className="text-[16px] font-bold text-white">
                      {t("timesTitle")}
                    </span>
                  </div>

                  {isCheckingAvailability ? (
                /* We do not know what is free yet: show inert placeholders
                   rather than pills that look bookable. */
                <div className="flex flex-col gap-3" aria-busy="true" aria-label={t("timesChecking")}>
                      {timeSlotLabels.map(label_0 => <span key={label_0} aria-hidden="true" className="min-h-12 animate-pulse rounded-full border border-white/8 bg-white/4" />)}
                      <p className="m-0 text-center text-[13px] text-white/40">
                        {t("timesChecking")}
                      </p>
                    </div>) : selectedDate ? <div className="flex flex-col gap-3">
                      {availabilityFailed ? (
                  /* The endpoint is unreachable. Booking stays open --
                     the server revalidates the slot on submit anyway --
                     but the visitor is told the list is unverified. */
                  <p role="status" className="m-0 flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/4 px-4 py-3 text-[12px] leading-[1.7] text-white/60">
                          <span>{t("timesUnverified")}</span>
                          <button type="button" onClick={retryAvailability} className="text-[12px] font-bold underline underline-offset-4 transition-colors duration-200 hover:text-white" style={{
                      color: ACCENT
                    }}>
                            {t("timesRetry")}
                          </button>
                        </p>) : null}
                      {timeSlotLabels.map((label_1, index_0) => {
                    const available = isSlotAvailable(selectedDate, index_0, availability);
                    const isSelected_0 = selectedSlot === index_0;

                    // Unavailable slots lose their pill entirely in the
                    // design — they read as plain greyed-out text.
                    if (!available) {
                      return <span key={TIME_SLOT_VALUES[index_0]} aria-disabled="true" className="flex min-h-12 items-center justify-center px-4 text-[15px] text-white/20">
                              {label_1}
                            </span>;
                    }
                    return <label key={TIME_SLOT_VALUES[index_0]} className={`relative flex min-h-12 cursor-pointer items-center justify-center rounded-full border px-12 text-[15px] transition-all duration-200 ${isSelected_0 ? "border-transparent font-bold text-white shadow-[0_8px_22px_rgba(12,165,240,0.28)]" : "border-white/10 text-white/85 hover:border-white/22 hover:text-white"}`} style={isSelected_0 ? {
                      backgroundColor: ACCENT
                    } : undefined}>
                            <input type="radio" name="time-slot" value={TIME_SLOT_VALUES[index_0]} checked={isSelected_0} onChange={() => {
                        setSlotOverride(index_0);
                        setIsConfirmed(false);
                      }} className="sr-only" />
                            <span>{label_1}</span>
                            <span aria-hidden="true" className={`absolute start-4 flex size-4.5 shrink-0 items-center justify-center rounded-full border ${isSelected_0 ? "border-white bg-white" : "border-white/40"}`} />
                          </label>;
                  })}
                    </div> : <p className="m-0 text-[14px] text-white/44">
                      {t("timesEmpty")}
                    </p>}
                </fieldset>
              </div>
            </div>

            {/* Details form */}
            <div className={`flex flex-col gap-5 p-5 sm:p-6 ${CARD}`}>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <UserIcon className="size-5 text-white" />
                  <h2 className="m-0 text-[18px] font-bold leading-[1.6] text-white">
                    {t("formTitle")}
                  </h2>
                </div>
                <p className="m-0 text-[14px] leading-[1.8] text-white/44">
                  {t("formSubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                <label className="flex flex-col gap-3">
                  <span className="text-[15px] font-medium text-white/77">
                    {t("fields.nameLabel")}
                  </span>
                  <input type="text" name="name" autoComplete="name" required value={form.name} onChange={event_0 => setForm(prev => ({
                  ...prev,
                  name: event_0.target.value
                }))} placeholder={t("fields.namePlaceholder")} className={FIELD} />
                </label>

                <label className="flex flex-col gap-3">
                  <span className="text-[15px] font-medium text-white/77">
                    {t("fields.emailLabel")}
                  </span>
                  <input type="email" name="email" autoComplete="email" required dir="ltr" value={form.email} onChange={event_1 => setForm(prev_0 => ({
                  ...prev_0,
                  email: event_1.target.value
                }))} placeholder={t("fields.emailPlaceholder")} className={`${FIELD} ${isArabic ? "text-right" : ""}`} />
                </label>

                <label className="flex flex-col gap-3">
                  <span className="text-[15px] font-medium text-white/77">
                    {t("fields.companyLabel")}
                  </span>
                  <input type="text" name="company" autoComplete="organization" value={form.company} onChange={event_2 => setForm(prev_1 => ({
                  ...prev_1,
                  company: event_2.target.value
                }))} placeholder={t("fields.companyPlaceholder")} className={FIELD} />
                </label>

                <label className="flex flex-col gap-3">
                  <span className="text-[15px] font-medium text-white/77">
                    {t("fields.phoneLabel")}
                  </span>
                  <input type="tel" name="phone" autoComplete="tel" required dir="ltr" value={form.phone} onChange={event_3 => setForm(prev_2 => ({
                  ...prev_2,
                  phone: event_3.target.value
                }))} placeholder={t("fields.phonePlaceholder")} className={`${FIELD} ${isArabic ? "text-right" : ""}`} />
                </label>

                <label className="flex flex-col gap-3 sm:col-span-2">
                  <span className="text-[15px] font-medium text-white/77">
                    {t("fields.notesLabel")}
                  </span>
                  <textarea name="notes" rows={4} value={form.notes} onChange={event_4 => setForm(prev_3 => ({
                  ...prev_3,
                  notes: event_4.target.value
                }))} placeholder={t("fields.notesPlaceholder")} className={`${FIELD} min-h-26 resize-y`} />
                </label>
              </div>
            </div>
          </div>

          {/* ── Summary sidebar ────────────────────────────────────────── */}
          <aside className="flex flex-col gap-8 lg:sticky lg:top-28 lg:gap-12">
            <div className={`flex flex-col gap-7 p-6 sm:p-8 ${CARD_DASHED}`}>
              <div className="flex items-center gap-2.5 border-b border-white/10 pb-6">
                <CalendarCheckIcon className="size-5.5 text-white" />
                <h2 className="m-0 text-[19px] font-bold text-white">
                  {t("summaryTitle")}
                </h2>
              </div>

              <div className="flex flex-col gap-3 border-b border-white/10 pb-7">
                <p className="m-0 text-[18px] font-bold leading-[1.5] text-white">
                  {t(`services.${service}.title`)}
                </p>
                <p className="m-0 text-[14px] leading-[1.85] text-white/44">
                  {t(`services.${service}.summary`)}
                </p>
              </div>

              <dl className="m-0 flex flex-col gap-6">
                {summaryRows.map(({
                key,
                icon: RowIcon,
                label: label_2,
                value: value_0
              }) => <div key={key} className="flex items-center justify-between gap-3">
                    {/* The design trails the icon at the far end of the row and
                        right-aligns the label/value stack against it. */}
                    <div className="order-1 flex min-w-0 flex-col gap-1 text-start">
                      <dt className="text-[13px] text-white/44">{label_2}</dt>
                      <dd className="m-0 text-[17px] font-bold leading-[1.5] text-white">
                        {value_0}
                      </dd>
                    </div>
                    <RowIcon className="order-2 size-5.5 shrink-0 text-white/70" />
                  </div>)}
              </dl>
            </div>

            <div className={`flex flex-col gap-6 p-6 sm:p-8 ${CARD_DASHED}`}>
              <div className="flex items-center gap-2.5">
                <LayersIcon className="size-5.5 text-white" />
                <h2 className="m-0 text-[19px] font-bold text-white">
                  {t("benefitsTitle")}
                </h2>
              </div>

              <ul className="m-0 flex list-none flex-col gap-5 p-0">
                {benefits.map(benefit => <li key={benefit} className="flex items-center gap-3">
                    <CheckCircleIcon className="size-5 shrink-0 text-cyan-500" />
                    <span className="text-[15px] leading-[1.6] text-white/85">
                      {benefit}
                    </span>
                  </li>)}
              </ul>

              <div className="flex flex-col gap-3 border-t border-white/10 pt-6">
                <div className="flex items-center gap-2.5">
                  <ShieldIcon className="size-5.5 text-white" />
                  <span className="text-[18px] font-bold text-white">
                    {t("trustTitle")}
                  </span>
                </div>
                <p className="m-0 text-[14px] leading-[1.85] text-white/60">
                  {t("trustDescription")}
                </p>
              </div>
            </div>

            {/* Honeypot: off-screen and hidden from assistive tech. */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={event_5 => setWebsite(event_5.target.value)} className="pointer-events-none absolute left-[-9999px] size-0 opacity-0" />

            {submitError && !isConfirmed ? <p role="alert" className="m-0 rounded-xl border border-magenta-700/50 bg-magenta-900/20 px-4 py-3 text-[12px] leading-[1.7] text-white">
                {submitError}
              </p> : null}

            {showError && !isConfirmed ? <p role="alert" className="m-0 rounded-xl border border-magenta-700/50 bg-magenta-900/20 px-4 py-3 text-[12px] leading-[1.7] text-white">
                {t("confirmError")}
              </p> : null}

            {isConfirmed ? <div className={`flex flex-col gap-4 p-6 sm:p-8 ${CARD_DASHED}`}>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2.5">
                    <CheckCircleIcon className="size-5.5 text-cyan-500" />
                    <span className="text-[18px] font-bold text-white">
                      {t("confirmedTitle")}
                    </span>
                  </div>
                  <p className="m-0 text-[14px] leading-[1.85] text-white/60">
                    {t("confirmedDescription")}
                  </p>
                </div>

                {/* The booking is already recorded and the confirmation email
                    has gone out, so WhatsApp is an optional way to reach us
                    faster — not the thing that submits the request. */}
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border px-5 text-[16px] font-bold transition-colors duration-300" style={{
              borderColor: ACCENT_BUTTON,
              color: ACCENT_BUTTON
            }}>
                  <WhatsappIcon className="size-5" />
                  <span>{t("whatsappCta")}</span>
                </a>

                <button type="button" onClick={() => setIsConfirmed(false)} className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/16 px-5 text-[14px] font-medium text-white/77 transition-colors duration-300 hover:border-white/32 hover:text-white">
                  {t("editCta")}
                </button>
              </div> : <button type="submit" disabled={isSubmitting || isCheckingAvailability} aria-busy={isSubmitting || isCheckingAvailability} className="inline-flex min-h-12 w-full items-center justify-center gap-2.5 rounded-full px-5 text-[16px] font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60" style={{
            backgroundColor: ACCENT_BUTTON
          }}>
                {isSubmitting || isCheckingAvailability ? <span aria-hidden="true" className="size-5 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <CalendarClockIcon className="size-5.5" />}
                <span>
                  {isCheckingAvailability ? t("timesChecking") : isSubmitting ? t("submitting") : t("confirmCta")}
                </span>
              </button>}
          </aside>
        </form>
      </div>
    </section>;
}